import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";

const BASE = "/admin/campus/";

export interface ProductRow {
	id: number;
	waCode: string | null;
	kind: string;
	kindLabel: string;
	name: string;
	sku: string;
	legacySku: string | null;
	price: number;
	/** Producto sin precio aprobado: se puede comprar, no vender. */
	pricePending?: boolean;
	/** Precio sugerido esperando a Super Admin. */
	suggestedPrice?: number | null;
	sizeType: string | null;
	sizeValue: string | null;
	modelId: number | null;
	variantCount: number;
	trackStock: boolean;
	requiresSizeAtDelivery: boolean;
	hasPhoto: boolean;
	barcode: string | null;
	onHand: number;
	deprecatedAt: string | null;
	replacedByArticleId: number | null;
	versionLabel: string | null;
	tier: string | null;
}

export interface KitComponentRow {
	id?: number;
	articleId: number;
	name: string;
	waCode: string | null;
	kind: string | null;
	sizeValue: string | null;
	quantity: number;
	optional: boolean;
	sizeAtDelivery: boolean;
	notes: string | null;
	substitutes: { articleId: number; name: string; waCode: string | null; sizeValue: string | null }[];
}

export interface DeliveryOption {
	articleId: number;
	name: string;
	waCode: string | null;
	sizeValue: string | null;
	barcode: string | null;
	isSubstitute: boolean;
	onHand: number;
	/** Pendiente de recibir de órdenes de compra aprobadas hacia esta bodega. */
	inTransit?: number;
	eta?: string | null;
}

export interface DeliveryLine {
	componentId: number | null;
	name: string;
	needed: number;
	delivered: number;
	pending: number;
	optional: boolean;
	sizeAtDelivery: boolean;
	options: DeliveryOption[];
}

export interface DeliveryPlan {
	/** false = producto del catálogo viejo: se entrega sin descontar inventario. */
	tracksInventory?: boolean;
	paidInFull?: boolean;
	/** Producto del catálogo nuevo que aún no está pagado completo: no se entrega. */
	blockedReason?: string | null;
	kitId: number;
	kitName: string;
	quantity: number;
	deliveryStatusId: number;
	warehouse: { id: number; code: string; name: string };
	warehouseOptions?: { id: number; code: string; name: string; kind: string; countryCode: string; campusId: number | null }[];
	lines: DeliveryLine[];
	pendingTotal: number;
	deliveredItems: { id: number; name: string; sizeValue: string | null; quantity: number; author: string; createdAt: string | null }[];
	isTotal?: boolean;
}

/** Productos e inventario (Fase 1, Sep 2026): catálogo, kits compuestos, bodegas y entrega pieza por pieza. */
export class InventoryService {

	private clean(filters: Record<string, any>): Record<string, any> {
		const params: Record<string, any> = {};
		Object.keys(filters || {}).forEach((key) => {
			const value = filters[key];
			if (value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length < 1)) params[key] = value;
		});
		return params;
	}

	/** Los arreglos viajan como JSON dentro del multipart. */
	private form(data: Record<string, any>, photo?: File | null): FormData {
		const body = new FormData();
		Object.keys(data).forEach((key) => {
			const value = data[key];
			if (value === undefined || value === null) return;
			body.append(key, Array.isArray(value) || typeof value === "object" ? JSON.stringify(value) : String(value));
		});
		if (photo) body.append("photo", photo);
		return body;
	}

	// ---- catálogo

	catalogs = async (campusId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_catalogs`);

	list = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products`, { params: { ...this.clean(filters), page, perPage } });

	get = async (campusId: number, productId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}`);

	create = async (campusId: number, data: Record<string, any>, photo?: File | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products`, this.form(data, photo));

	update = async (campusId: number, productId: number, data: Record<string, any>, photo?: File | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_update`, this.form(data, photo));

	deprecate = async (campusId: number, productId: number, reason: string, replacedByArticleId?: number | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_deprecate`, { reason, replacedByArticleId: replacedByArticleId || null });

	restore = async (campusId: number, productId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_restore`, { reason });

	createVariants = async (campusId: number, productId: number, sizes: string[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_variants`, { sizes });

	photoUrl = (campusId: number, productId: number): string =>
		`${axiosInstance.defaults.baseURL || ""}${BASE}${campusId}/products/${productId}/_photo`;

	/** La foto va tras autenticación: se descarga como blob y se usa como objeto URL. */
	photoBlob = async (campusId: number, productId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_photo`, { responseType: "blob" });

	// ---- kits compuestos

	versions = async (campusId: number, productId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_versions`);

	saveVersion = async (campusId: number, productId: number, data: Record<string, any>): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/${productId}/_versions`, data);

	availability = async (campusId: number, productId: number, countryCode?: string): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_availability`, { params: countryCode ? { countryCode } : {} });

	// ---- kits por curso

	marginSales = async (campusId: number, filters: { from?: string; to?: string; band?: string }): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_sales`, { params: filters });

	riskyPrices = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_prices`);

	priceRequests = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_priceRequests`);

	approvePrice = async (campusId: number, requestId: number, price: number | null, note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_approvePrice/${requestId}`, { price, note });

	rejectPrice = async (campusId: number, requestId: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_rejectPrice/${requestId}`, { reason });

	courseKits = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_courseKits`);

	saveCourseKits = async (campusId: number, courseId: number, kits: any[]): Promise<AxiosResponse<any>> =>
		axiosInstance.put(`${BASE}${campusId}/products/_courseKits/${courseId}`, { kits });

	// ---- bodegas

	warehouses = async (campusId: number, countryCode?: string): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/warehouses`, { params: countryCode ? { countryCode } : {} });

	stock = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/warehouses/_stock`, { params: { ...this.clean(filters), page, perPage } });

	movement = async (campusId: number, data: Record<string, any>, photo?: File | null): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/warehouses/_movement`, this.form(data, photo));

	movements = async (campusId: number, productId: number, warehouseId: number | null, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/${productId}/_movements`, { params: { warehouseId: warehouseId || undefined, page, perPage } });

	// ---- entrega pieza por pieza

	deliveryPlan = async (campusId: number, kitId: number, warehouseId?: number | null): Promise<AxiosResponse<DeliveryPlan>> =>
		axiosInstance.get(`${BASE}${campusId}/kits/${kitId}/_deliveryPlan`, { params: warehouseId ? { warehouseId } : {} });

	registerDeliveryItems = async (campusId: number, kitId: number, items: any[], note: string, warehouseId?: number | null): Promise<AxiosResponse<{ message: string; plan: DeliveryPlan }>> =>
		axiosInstance.post(`${BASE}${campusId}/kits/${kitId}/_deliveryItems`, { items, note, warehouseId: warehouseId || undefined });
}
